"use client";

import styles from "./PriceDisplay.module.scss";
import Button from "@components/atoms/Button";
import assetLoader from "@utils/internalImageLoder";
import Image from "@components/atoms/Image";
import { formatDeliveryDate } from "@utils/formatDate";
import { useEffect, useRef, useState } from "react";
import CitySelectionModal from "@components/organisms/CitySelectionModal";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import { getLoginUrl, addCaptureIntentIfNeeded } from "@utils/getLoginUrl";
import RequestCallBackModal from "../RequestCallBackModal";
import WishlistButton from "@components/atoms/WishlistButton";
import WishlistLimitModal from "../WishlistLimitModal";
import WishlistToast from "@components/atoms/WishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { useWishlistActions } from "@hooks/useWishlistActions";
import { PageType } from "@constants/pageTypes";
import { useRouter, usePathname } from "next/navigation";
import { getPlatform } from "@utils/platformDetection.client";
import { postLead } from "@services/leadCaptureService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import TrustMarkerBadge from "@components/atoms/TrustMarkerBadge";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { formatPrice } from "@utils/numberFormatter";
import { getLeadData } from "@utils/leadDataStorage";

interface PriceDisplayProps {
  title?: string;
  minPrice: number;
  maxPrice: number;
  minAdPriceDetails: any;
  cityName: string;
  waitingPeriod: string;
  onLocationClick?: () => void;
  className?: string;
  isLoggedIn: boolean;
  experessDelivery: boolean;
  city?: any;
  minAckodriveSavings?: any;
  brandName?: string;
  modelName?: string;
  pageType?: PageType;
  slug?: string;
  isSellable: boolean;
  carAvailability?: string;
  trustMarkerBadge?: string | null;
}

export default function PriceDisplay({
  title,
  minPrice,
  minAdPriceDetails,
  maxPrice,
  cityName,
  waitingPeriod,
  className,
  isLoggedIn,
  experessDelivery,
  city,
  minAckodriveSavings,
  brandName,
  modelName,
  slug,
  isSellable,
  carAvailability,
  pageType,
  trustMarkerBadge,
}: PriceDisplayProps) {
  const hasBottomNav = useHasBottomNav();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const mmvDetails = { make: brandName, model: modelName };
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  // Use the custom wishlist actions hook
  const {
    showLimitModal,
    pendingWishlistCar,
    showWishlist,
    car,
    toast,
    wishlistItems,
    handleWishlistToggle,
    handleUndo,
    handleViewWishlist,
    handleReplace,
    closeLimitModal,
    isCarWishlisted,
  } = useWishlistActions({
    slug,
    brandName,
    modelName,
    isLoggedIn,
  });

  const leadCaptureHandler: () => Promise<void> = async () => {
    let leadData: any = {
      city: cityName,
      make: brandName || "",
      model: modelName || "",
      variant: "",
      color: "",
    };
    // const storedSource = sessionStorage.getItem("referrerSource");
    const utmTerm =
      new URL(window.location.href).searchParams.get("utm_term") || "";
    leadData.dump_data = {
      mx_Platform: getPlatform(),
      mx_Page_Type_Web: pageType,
      mx_Login_Type: "Get quote",
      // SourceReferrerURL: storedSource,
      mx_URL: window.location.href || process.env.NEXT_PUBLIC_FRONTEND_HOST,
      mx_UTM_Term: utmTerm,
      mx_color: leadData?.color || "",
    };

    // Get existing lead data with fallback to localStorage
    const existingLeadData = await getLeadData();
    const mergedLeadData = { ...existingLeadData, ...leadData };
    leadData = mergedLeadData;
    const buyingPeriod = leadData?.buying_period || "";
    try {

      console.log("additionalLeaddata1", leadData);
      const leadResponse = await postLead(addUTMParamsToLeadData(leadData));
      if (!leadResponse.success) {
        console.error("Lead capture failed.", leadResponse);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            city_name: cityName,
            login_type: "Get quote",
            intent: buyingPeriod,
          },
        });
      } else {
        const leadCreated = !!leadResponse?.data?.is_created;
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            action_type: leadCreated ? "lead_created" : "lead_updated",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            city_name: cityName,
            login_type: "Get quote",
            intent: buyingPeriod,
          },
        });
      }
    } catch (error) {
      console.error("Error in postLead API call:", error);
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          exception_type: "API issue",
          exception_name: "failed lead creation",
          car_availability: carAvailability,
          vehicle_make: brandName,
          vehicle_model: modelName,
          city_name: cityName,
          login_type: "Get quote",
          intent: buyingPeriod,
        },
      });
    }
  };

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setShowStickyCTA(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    if (ctaRef.current) {
      observer.observe(ctaRef.current);
    }
    return () => {
      if (ctaRef.current) observer.unobserve(ctaRef.current);
    };
  }, []);

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  return (
    <div className={`${styles.container} ${className || ""}`}>
      {isModalOpen && (
        <CitySelectionModal
          selectedCity={city}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}
      <div className={styles.priceRow}>
        {trustMarkerBadge && (
          <div className={styles.trustMarkerRow}>
            <TrustMarkerBadge label={trustMarkerBadge} />
            {showWishlist && car && (
              <WishlistButton
                isLoggedIn={isLoggedIn}
                isInWishlist={isCarWishlisted(car?.id)}
                onWishlistClick={() =>
                  handleWishlistToggle(brandName!, modelName!, car)
                }
                size="small"
                className={styles.wishlistButton}
                model_name={modelName}
                brand_name={brandName}
              />
            )}
          </div>
        )}

        {!trustMarkerBadge && (
          <div className={styles.header}>
            {title && <h1 className={styles.title}>{title}</h1>}
            {showWishlist && car && (
              <WishlistButton
                isLoggedIn={isLoggedIn}
                isInWishlist={isCarWishlisted(car?.id)}
                onWishlistClick={() =>
                  handleWishlistToggle(brandName!, modelName!, car)
                }
                size="small"
                className={styles.wishlistButton}
                model_name={modelName}
                brand_name={brandName}
              />
            )}
          </div>
        )}

        {trustMarkerBadge && title && (
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{title}</h1>
          </div>
        )}
        <div
          className={styles.locationInfo}
          onClick={() => setIsModalOpen(true)}
        >
          {(!isLoggedIn || !isSellable) && (
            <span className={styles.desktopPrice}>
              {`${formatPrice(minPrice)} — ${formatPrice(
                maxPrice
              )}`}
            </span>
          )}
          <span>
            {isSellable && isLoggedIn
              ? `ACKO Drive price starts from`
              : `On-road price in ${cityName}`}
          </span>
          {(!isLoggedIn || !isSellable) && (
            <Button
              size="ds"
              onButtonClick={() => setIsModalOpen(true)}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_cities",
                  section: "Top fold",
                  car_availability: carAvailability,
                  city_name: cityName,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                },
              }}
            >
              <Image
                src={`${assetLoader()}/icons/expertReview/Chevron_down.svg`}
                alt={"Right Arrow"}
                className={styles.chevronDown}
                width={20}
                height={20}
                priority={true}
              />
            </Button>
          )}
        </div>
        {isSellable && isLoggedIn ? (
          <span className={styles.LoggedInprice}>
            <div className={styles.minAdPrice}>
              {`${formatPrice(minAdPriceDetails?.ackodrivePrice)}`}
            </div>
            <div className={styles.minPrice}>
              {`${formatPrice(minAdPriceDetails?.onroadPrice)}`}
            </div>
            <div className={styles.minSaving}>
              {`Save ₹${getIndianCurrencyFormat(
                minAdPriceDetails?.ackodriveSavings
              )}`}
            </div>
          </span>
        ) : (
          <span className={styles.mobilePrice}>
            {`${formatPrice(minPrice)} — ${formatPrice(
              maxPrice
            )}`}
          </span>
        )}
        {isSellable &&
          (isLoggedIn ? (
            <div className={styles.horizontalLine} />
          ) : (
            <span className={styles.minimumSaving}>
              {`Get minimum savings of ${getIndianCurrencyFormat(
                minAckodriveSavings
              )} on ACKO Drive`}
            </span>
          ))}
        {isSellable && (
          <div
            className={`${styles.CTADeliveryWrapper} ${
              !isLoggedIn && styles.logoutCTADeliveryWrapper
            }`}
          >
            <Button
              ref={ctaRef}
              size="ds"
              className={styles.exclusivePriceCTA}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: isLoggedIn
                    ? "tap_btn_get_detailed_quote"
                    : "tap_btn_login_to_save_mm",
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  city_name: cityName,
                  car_availability: carAvailability,
                  section: "Top fold",
                },
              }}
              onButtonClick={async () => {
                if (isLoggedIn) {
                  showLoader("Processing your request...");
                  try {
                    await leadCaptureHandler();
                    router.push(`/variant-selection/${slug}`);
                  } catch (error) {
                    console.error("Error in lead capture:", error);
                    hideLoader();
                  }
                } else {
                  showLoader("Redirecting to login...");
                  const currentUrl = addCaptureIntentIfNeeded(
                    window.location.href,
                    mmvDetails,
                    "Reveal price",
                    pageType
                  );
                  hideLoader();
                  await handleLoginFlow(getLoginUrl(currentUrl), LoginSdkConfig.AD_PRICE);
                }
              }}
            >
              {isLoggedIn ? `Get a quote` : `Unlock exclusive price`}
            </Button>

            {experessDelivery && (
              <div className={styles.expressDeliveryWrapper}>
                <Image
                  src={`${assetLoader()}/icons/HeroSection/ExpressDeliveryPurple.svg`}
                  alt={"Right Arrow"}
                  width={14}
                  height={14}
                  priority={true}
                />
                {isLoggedIn ? (
                  <>
                    Express delivery by{" "}
                    <span className={styles.deliveryDate}>
                      {formatDeliveryDate(parseInt(waitingPeriod))}
                    </span>
                  </>
                ) : (
                  `Express delivery available on select variants`
                )}
              </div>
            )}
          </div>
        )}

        {isSellable && showStickyCTA && (
          <div className={styles.stickyCTAWrapper}>
            {!isLoggedIn && (
              <div className={styles.stickyInfo}>
                Unlock ACKO Drive’s exclusive pricing, offers, and more...
              </div>
            )}
            {isLoggedIn ? (
              <div className={styles.stickyDualCTA}>
                <Button
                  size="ds"
                  variant="outline"
                  className={styles.stickyTalkToUsCTA}
                  onButtonClick={() => {
                    setIsRequestCallBackModalOpen(true);
                  }}
                  segmentEventProps={{
                    eventName: "action",
                    pageType: pageType,
                    eventProperties: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_help",
                      vehicle_make: brandName,
                      vehicle_model: modelName,
                      city_name: cityName,
                      car_availability: carAvailability,
                      button_text: "Talk to us",
                      button_location: "sticky_cta",
                    },
                  }}
                >
                  Talk to us
                </Button>
                <Button
                  size="ds"
                  className={styles.stickyExclusivePriceCTA}
                  // link="./variants"
                  onButtonClick={() => {
                    showLoader("Processing your request...");
                    router.push(`/variant-selection/${slug}`);
                  }}
                  segmentEventProps={{
                    eventName: "action",
                    pageType: pageType,
                    eventProperties: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_get_detailed_quote",
                      vehicle_make: brandName,
                      vehicle_model: modelName,
                      city_name: cityName,
                      car_availability: carAvailability,
                      button_text: "Get a quote",
                      button_location: "sticky_cta",
                    },
                  }}
                >
                  Get a quote
                </Button>
              </div>
            ) : (
              <Button
                size="ds"
                className={styles.stickyExclusivePriceCTALoggedOut}
                onButtonClick={async () => {
                  showLoader("Redirecting to login...");
                  if (typeof window !== "undefined") {
                    sessionStorage.setItem("loginType", "Reveal price");
                    localStorage.setItem(
                      "MMV",
                      JSON.stringify({
                        make: mmvDetails?.make || "",
                        model: mmvDetails?.model || "",
                        variant: "",
                        color: "",
                      })
                    );
                  }
                  const loginUrl = getLoginUrl(
                    addCaptureIntentIfNeeded(
                      window.location.href,
                      mmvDetails,
                      "Reveal price",
                      pageType
                    )
                  );
                  hideLoader();
                  await handleLoginFlow(loginUrl, LoginSdkConfig.EXCLUSIVE_OFFER);
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_login_to_save_mm",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    section: "Sticky CTA",
                  },
                }}
              >
                Log in to view
              </Button>
            )}
          </div>
        )}
      </div>

      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => {
            setIsRequestCallBackModalOpen(false);
          }}
        />
      )}
      {/* Toast notification */}
      <WishlistToast
        isVisible={toast.show}
        isAdding={toast.isAdding}
        carMake={toast.carMake}
        carName={toast.carName}
        hasBottomNav={hasBottomNav}
        onViewWishlist={handleViewWishlist}
        onUndo={handleUndo}
      />

      {/* Wishlist limit modal */}
      {showLimitModal && pendingWishlistCar && (
        <WishlistLimitModal
          isOpen={showLimitModal}
          onClose={closeLimitModal}
          wishlistedCars={wishlistItems}
          onReplace={handleReplace}
          newCar={pendingWishlistCar}
        />
      )}
    </div>
  );
}
